import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Calendar, MapPin, CheckCircle2, Globe } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Detect the visitor's preferred language from the browser. Falls back to
// English when the browser locale is anything other than an Arabic dialect.
function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const langs = [navigator.language, ...(navigator.languages || [])];
  for (const l of langs) {
    if (!l) continue;
    if (l.toLowerCase().startsWith("ar")) return "ar";
  }
  return "en";
}

const COPY = {
  en: {
    title: "Reserve your spot",
    subtitle: "Takes 20 seconds. No account required.",
    submit: "Confirm RSVP",
    submitting: "Submitting…",
    thanksTitle: "You're in.",
    thanksBody: "We've saved your RSVP. See you there!",
    capacity: "Capacity",
    selectPlaceholder: "Select…",
    yes: "Yes",
    required: "required",
    invalidLink: "This event link is no longer valid.",
    loadError: "Could not load event",
    submitError: "Submission failed",
    langLabel: "العربية",
  },
  ar: {
    title: "احجز مقعدك",
    subtitle: "تستغرق 20 ثانية. لا حاجة لحساب.",
    submit: "تأكيد الحضور",
    submitting: "جارٍ الإرسال…",
    thanksTitle: "تم تسجيلك.",
    thanksBody: "حفظنا حجزك. نراك هناك!",
    capacity: "السعة",
    selectPlaceholder: "اختر…",
    yes: "نعم",
    required: "مطلوب",
    invalidLink: "لم يعد رابط الفعالية صالحًا.",
    loadError: "تعذّر تحميل الفعالية",
    submitError: "تعذّر الإرسال",
    langLabel: "English",
  },
};

/**
 * PublicEventPage — landing page a public visitor lands on after
 * scanning the event QR code. Auto-detects the visitor's browser
 * locale (English vs. Arabic) and renders the RSVP form accordingly,
 * with a manual language toggle for override. Renders the event
 * details + a form built from `rsvp_form_fields` (falls back to
 * name/email/phone/organization). Submissions post to the public
 * endpoint, no auth required.
 */
export default function PublicEventPage() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [lang, setLang] = useState(detectBrowserLang);
  const copy = COPY[lang] || COPY.en;
  const isAr = lang === "ar";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/public/${token}`);
      setEvent(res.data);
      const empty = {};
      for (const f of res.data.rsvp_form_fields || []) empty[f.key] = "";
      setForm(empty);
    } catch (e) {
      setError(e?.response?.status === 404 ? COPY[lang].invalidLink : COPY[lang].loadError);
    }
    // load runs once per token; language switches shouldn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/events/public/${token}/rsvp`, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || copy.submitError);
    } finally {
      setSaving(false);
    }
  };

  const dt = useMemo(() => (event?.date_time ? new Date(event.date_time) : null), [event?.date_time]);
  const dtLabel = useMemo(() => {
    if (!dt) return "";
    try {
      return dt.toLocaleString(isAr ? "ar-SA" : "en-US", {
        dateStyle: "full",
        timeStyle: "short",
      });
    } catch {
      return dt.toLocaleString();
    }
  }, [dt, isAr]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6" dir={isAr ? "rtl" : "ltr"} data-testid="public-event-error">
        <Card className="max-w-md rounded-2xl border border-border p-8 text-center space-y-4">
          <p className="text-destructive font-medium">{error}</p>
          <button
            type="button"
            onClick={() => setLang(isAr ? "en" : "ar")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary transition-colors mx-auto"
            data-testid="public-event-lang-toggle"
            aria-label="Switch language"
          >
            <Globe className="h-3.5 w-3.5" />
            {copy.langLabel}
          </button>
        </Card>
      </div>
    );
  }
  if (!event) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-muted/20 py-10" dir={isAr ? "rtl" : "ltr"}>
      <SEO
        title={event.title}
        description={event.description || `${event.title} — The Saudi Gate`}
        path={`/e/${token}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: event.title,
          description: event.description || "",
          startDate: event.starts_at || event.date || undefined,
          endDate: event.ends_at || undefined,
          eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
          location: event.location
            ? { "@type": "Place", name: event.location, address: event.location }
            : { "@type": "VirtualLocation", url: typeof window !== "undefined" ? window.location.href : "" },
          organizer: { "@type": "Organization", name: "The Saudi Gate" },
        }}
      />
      <div className="mx-auto max-w-2xl px-4 space-y-6" data-testid="public-event-page">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setLang(isAr ? "en" : "ar")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary transition-colors"
            data-testid="public-event-lang-toggle"
            aria-label="Switch language"
          >
            <Globe className="h-3.5 w-3.5" />
            {copy.langLabel}
          </button>
        </div>

        <Card className="rounded-2xl border border-border overflow-hidden">
          {event.cover_url && (
            <img src={event.cover_url} alt="" className="w-full h-48 object-cover" />
          )}
          <div className="p-6 space-y-3">
            <h1 className="font-display text-2xl font-semibold" data-testid="public-event-title">{event.title}</h1>
            <div className="flex items-center flex-wrap gap-4 text-sm text-muted-foreground">
              {dt && (
                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{dtLabel}</span>
              )}
              {event.location && (
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{event.location}</span>
              )}
              {event.capacity > 0 && (
                <span>{copy.capacity} {event.capacity}</span>
              )}
            </div>
            {event.description && (
              <p className="text-sm whitespace-pre-wrap leading-relaxed pt-2">{event.description}</p>
            )}
          </div>
        </Card>

        <Card className="rounded-2xl border border-border p-6" data-testid="public-event-form">
          {done ? (
            <div className="py-6 text-center space-y-2" data-testid="public-event-thanks">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <p className="font-display text-lg font-semibold">{copy.thanksTitle}</p>
              <p className="text-sm text-muted-foreground">{copy.thanksBody}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 className="font-display font-semibold">{copy.title}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{copy.subtitle}</p>
              </div>
              {(event.rsvp_form_fields || []).map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">
                    {f.label}{f.required && <span className="text-destructive"> *</span>}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      rows={3}
                      required={!!f.required}
                      className="rounded-lg mt-1"
                      dir={isAr ? "rtl" : "ltr"}
                      data-testid={`public-event-field-${f.key}`}
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={!!f.required}
                      className="rounded-lg mt-1 w-full border border-input bg-transparent h-10 px-3 text-sm"
                      data-testid={`public-event-field-${f.key}`}
                    >
                      <option value="">{copy.selectPlaceholder}</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 mt-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                        data-testid={`public-event-field-${f.key}`}
                      />
                      {copy.yes}
                    </label>
                  ) : (
                    <Input
                      type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={!!f.required}
                      className="rounded-lg mt-1"
                      dir={isAr ? "rtl" : "ltr"}
                      data-testid={`public-event-field-${f.key}`}
                    />
                  )}
                </div>
              ))}
              <Button type="submit" className="btn-brand w-full rounded-xl gap-2" disabled={saving} data-testid="public-event-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? copy.submitting : copy.submit}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
